import { memo, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Controller, type Control } from "react-hook-form";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  type DragEndEvent,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

import {
  parseQuestionOptions,
  serializeQuestionOptions,
} from "src-next/shared/utils/questionnaireOptions";
import type {
  QuestionnaireFormValues,
  QuestionnaireQuestionFormValues,
} from "../types";
import QuestionFieldPreview from "./QuestionFieldPreview";

export interface ActiveQuestionFormProps {
  activeQuestionIndex: number;
  activeQuestion: QuestionnaireQuestionFormValues;
  control: Control<QuestionnaireFormValues>;
  Controller: typeof Controller;
}

interface SortableOptionReadRowProps {
  sortableId: string;
  optionText: string;
  optionIndex: number;
  onEditOption: (optionIndex: number) => void;
  onDeleteOption: (optionIndex: number) => void;
}

const SortableOptionReadRow = memo(
  ({
    sortableId,
    optionText,
    optionIndex,
    onEditOption,
    onDeleteOption,
  }: SortableOptionReadRowProps) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: sortableId });
    const style = {
      transform: transform ? `translate3d(0px, ${transform.y}px, 0)` : undefined,
      transition,
    };

    return (
      <Stack
        ref={setNodeRef}
        style={style}
        direction="row"
        spacing={1}
        alignItems="center"
      >
        <Box
          role="button"
          tabIndex={0}
          aria-label={`Editar opção ${optionIndex + 1}`}
          onClick={() => onEditOption(optionIndex)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onEditOption(optionIndex);
            }
          }}
          sx={{
            flex: 1,
            px: 1.5,
            py: 1.25,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            cursor: "pointer",
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
            },
          }}
        >
          <Typography variant="body2">{optionText}</Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={`Excluir opção ${optionIndex + 1}`}
          onClick={() => onDeleteOption(optionIndex)}
        >
          <DeleteOutlineIcon />
        </IconButton>
        <Box
          sx={{ display: "flex", alignItems: "center", cursor: "grab" }}
          aria-label={`Reordenar opção ${optionIndex + 1}`}
          {...listeners}
          {...attributes}
        >
          <DragIndicatorIcon sx={{ color: "text.disabled", fontSize: 20 }} />
        </Box>
      </Stack>
    );
  },
);

SortableOptionReadRow.displayName = "SortableOptionReadRow";

const ActiveQuestionForm = memo(
  ({
    activeQuestionIndex,
    activeQuestion,
    control,
    Controller,
  }: ActiveQuestionFormProps) => {
    const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(
      null,
    );
    const [draftOptionText, setDraftOptionText] = useState("");
    const [isCreatingNewOption, setIsCreatingNewOption] = useState(false);
    const optionInputRef = useRef<HTMLInputElement | null>(null);
    const optionDndSensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    useEffect(() => {
      if (editingOptionIndex === null) return;
      optionInputRef.current?.focus();
      optionInputRef.current?.select();
    }, [editingOptionIndex, isCreatingNewOption]);

    useEffect(() => {
      setEditingOptionIndex(null);
      setDraftOptionText("");
      setIsCreatingNewOption(false);
    }, [activeQuestion.id]);

    return (
      <Stack spacing={2} key={activeQuestion.id}>
      <Stack spacing={2} direction="row">
        <Controller
          name={`questions.${activeQuestionIndex}.label` as const}
          control={control}
          render={({ field }) => (
            <TextField label="Enunciado da pergunta" fullWidth {...field} />
          )}
        />
        <Controller
          name={`questions.${activeQuestionIndex}.isRequired` as const}
          control={control}
          render={({ field }) => (
            <FormControlLabel
              sx={{ flex: 1, minWidth: 250 }}
              control={
                <Switch
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label="Resposta obrigatória"
            />
          )}
        />
      </Stack>
      <Controller
        name={`questions.${activeQuestionIndex}.helperText` as const}
        control={control}
        render={({ field }) => (
          <TextField
            label="Texto auxiliar (opcional)"
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            {...field}
          />
        )}
      />
      <Stack spacing={2} alignItems={{ xs: "flex-start", sm: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
            Tipo de resposta
          </Typography>
          <Controller
            name={`questions.${activeQuestionIndex}.type` as const}
            control={control}
            render={({ field }) => (
              <ToggleButtonGroup
                exclusive
                value={field.value}
                onChange={(_event, nextValue) => {
                  if (!nextValue) return;
                  field.onChange(nextValue);
                }}
                aria-label="tipo de resposta da pergunta"
                sx={{ flexWrap: "wrap", gap: 0.5 }}
              >
                <ToggleButton value="shortText" aria-label="Texto curto">
                  Texto curto
                </ToggleButton>
                <ToggleButton value="longText" aria-label="Texto longo">
                  Texto longo
                </ToggleButton>
                <ToggleButton value="yesNo" aria-label="Sim ou Não">
                  Sim / Não
                </ToggleButton>
                <ToggleButton
                  value="multipleChoice"
                  aria-label="Múltipla escolha"
                >
                  Múltipla escolha
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          />
        </Box>
        {activeQuestion.type === "multipleChoice" && (
          <Stack spacing={1.5} sx={{ width: "100%" }}>
            <Controller
              name={`questions.${activeQuestionIndex}.allowMultipleAnswers` as const}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(event) =>
                        field.onChange(event.target.checked)
                      }
                    />
                  }
                  label="Permitir múltiplas respostas"
                />
              )}
            />

            <Controller
              name={`questions.${activeQuestionIndex}.optionsCsv` as const}
              control={control}
              render={({ field }) => {
                const options = parseQuestionOptions(field.value);
                const saveOptions = (nextOptions: string[]) => {
                  field.onChange(serializeQuestionOptions(nextOptions));
                };

                const updateEditingIndexAfterMove = (
                  movedFromIndex: number,
                  movedToIndex: number,
                ) => {
                  if (editingOptionIndex === null || isCreatingNewOption) return;

                  if (editingOptionIndex === movedFromIndex) {
                    setEditingOptionIndex(movedToIndex);
                    return;
                  }

                  const movedDown =
                    movedFromIndex < editingOptionIndex &&
                    editingOptionIndex <= movedToIndex;
                  if (movedDown) {
                    setEditingOptionIndex((previousIndex) =>
                      previousIndex === null ? previousIndex : previousIndex - 1,
                    );
                    return;
                  }

                  const movedUp =
                    movedToIndex <= editingOptionIndex &&
                    editingOptionIndex < movedFromIndex;
                  if (movedUp) {
                    setEditingOptionIndex((previousIndex) =>
                      previousIndex === null ? previousIndex : previousIndex + 1,
                    );
                  }
                };

                const startEditingExistingOption = (optionIndex: number) => {
                  setEditingOptionIndex(optionIndex);
                  setDraftOptionText(options[optionIndex] ?? "");
                  setIsCreatingNewOption(false);
                };

                const startCreatingOption = () => {
                  if (isCreatingNewOption) {
                    const normalizedValue = draftOptionText.trim();
                    if (normalizedValue.length > 0) {
                      saveOptions([...options, normalizedValue]);
                      setEditingOptionIndex(options.length + 1);
                      setDraftOptionText("");
                      setIsCreatingNewOption(true);
                      return;
                    }
                    optionInputRef.current?.focus();
                    return;
                  }
                  if (editingOptionIndex !== null) {
                    cancelEditing();
                  }
                  setEditingOptionIndex(options.length);
                  setDraftOptionText("");
                  setIsCreatingNewOption(true);
                };

                const cancelEditing = () => {
                  setEditingOptionIndex(null);
                  setDraftOptionText("");
                  setIsCreatingNewOption(false);
                };

                const commitEditing = () => {
                  if (editingOptionIndex === null) return;

                  const normalizedValue = draftOptionText.trim();

                  if (isCreatingNewOption) {
                    if (normalizedValue.length > 0) {
                      saveOptions([...options, normalizedValue]);
                    }
                    cancelEditing();
                    return;
                  }

                  if (
                    editingOptionIndex < 0 ||
                    editingOptionIndex >= options.length
                  ) {
                    cancelEditing();
                    return;
                  }

                  const nextOptions = [...options];
                  if (normalizedValue.length === 0) {
                    nextOptions.splice(editingOptionIndex, 1);
                  } else {
                    nextOptions[editingOptionIndex] = normalizedValue;
                  }
                  saveOptions(nextOptions);
                  cancelEditing();
                };

                const handleOptionDragEnd = (event: DragEndEvent) => {
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;

                  const activeId = String(active.id);
                  const overId = String(over.id);
                  const activeIndex = Number(activeId.replace("option-", ""));
                  const overIndex = Number(overId.replace("option-", ""));
                  if (
                    Number.isNaN(activeIndex) ||
                    Number.isNaN(overIndex) ||
                    activeIndex < 0 ||
                    overIndex < 0 ||
                    activeIndex >= options.length ||
                    overIndex >= options.length
                  ) {
                    return;
                  }

                  const nextOptions = [...options];
                  const [movedOption] = nextOptions.splice(activeIndex, 1);
                  nextOptions.splice(overIndex, 0, movedOption);
                  saveOptions(nextOptions);
                  updateEditingIndexAfterMove(activeIndex, overIndex);
                };

                return (
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={500}>
                      Opções
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: 220,
                        overflowY: "auto",
                        overflowX: "hidden",
                        pr: 0.5,
                        borderRadius: 1,
                        border: "1px dashed",
                        borderColor: "divider",
                        p: 1,
                      }}
                    >
                      <Stack spacing={1}>
                        <DndContext
                          sensors={optionDndSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleOptionDragEnd}
                        >
                          <SortableContext
                            items={options.map((_, optionIndex) => `option-${optionIndex}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            {options.map((optionValue, optionIndex) => {
                              const isEditingCurrentOption =
                                editingOptionIndex === optionIndex &&
                                !isCreatingNewOption;

                              if (isEditingCurrentOption) {
                                return (
                                  <Stack
                                    key={`edit-option-${optionIndex}`}
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <TextField
                                      label={`Opção ${optionIndex + 1}`}
                                      value={draftOptionText}
                                      onChange={(event) =>
                                        setDraftOptionText(event.target.value)
                                      }
                                      onBlur={commitEditing}
                                      onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                          event.preventDefault();
                                          commitEditing();
                                        }
                                        if (event.key === "Escape") {
                                          event.preventDefault();
                                          cancelEditing();
                                        }
                                      }}
                                      inputRef={optionInputRef}
                                      fullWidth
                                    />
                                    <IconButton
                                      aria-label={`Confirmar edição da opção ${optionIndex + 1}`}
                                      onMouseDown={(event) => event.preventDefault()}
                                      onClick={commitEditing}
                                    >
                                      <KeyboardReturnIcon />
                                    </IconButton>
                                  </Stack>
                                );
                              }

                              return (
                                <SortableOptionReadRow
                                  key={`option-${optionIndex}`}
                                  sortableId={`option-${optionIndex}`}
                                  optionText={optionValue}
                                  optionIndex={optionIndex}
                                  onEditOption={startEditingExistingOption}
                                  onDeleteOption={(targetIndex) => {
                                    const nextOptions = options.filter(
                                      (_option, index) => index !== targetIndex,
                                    );
                                    saveOptions(nextOptions);

                                    if (
                                      editingOptionIndex !== null &&
                                      !isCreatingNewOption &&
                                      editingOptionIndex > targetIndex
                                    ) {
                                      setEditingOptionIndex((previousIndex) =>
                                        previousIndex === null
                                          ? previousIndex
                                          : previousIndex - 1,
                                      );
                                    }
                                  }}
                                />
                              );
                            })}
                          </SortableContext>
                        </DndContext>

                        {isCreatingNewOption &&
                          editingOptionIndex === options.length && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                label="Novo item"
                                placeholder="Digite o texto da opção"
                                value={draftOptionText}
                                onChange={(event) =>
                                  setDraftOptionText(event.target.value)
                                }
                                onBlur={commitEditing}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    commitEditing();
                                  }
                                  if (event.key === "Escape") {
                                    event.preventDefault();
                                    cancelEditing();
                                  }
                                }}
                                inputRef={optionInputRef}
                                fullWidth
                              />
                              <IconButton
                                aria-label="Confirmar novo item"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={commitEditing}
                              >
                                <KeyboardReturnIcon />
                              </IconButton>
                            </Stack>
                          )}
                      </Stack>
                    </Box>

                    <Button
                      variant="text"
                      onClick={startCreatingOption}
                      sx={{ alignSelf: "flex-start" }}
                      aria-label="Adicionar novo item de opção"
                    >
                      Novo item
                    </Button>
                  </Stack>
                );
              }}
            />
          </Stack>
        )}
      </Stack>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "action.hover",
          p: 1.5,
          backgroundColor: "action.hover",
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <Chip
            size="small"
            label="Preview"
            icon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            color="default"
          />
          <Typography variant="body2" color="text.secondary">
            Somente leitura — exemplo de como o campo será apresentado
          </Typography>
        </Stack>
        <QuestionFieldPreview question={activeQuestion} />
      </Box>
    </Stack>
    );
  },
);

ActiveQuestionForm.displayName = "ActiveQuestionForm";

export default ActiveQuestionForm;
